import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Loader2, ShieldCheck, Briefcase, Users, DoorOpen, User as UserIcon, Sparkles, Clock, X } from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";
import { useAuth } from "../lib/AuthContext";
import api from "../lib/api";

const iconFor = {
  startup: Briefcase,
  investor: Users,
  deal_room: DoorOpen,
  user: UserIcon,
  match: Sparkles,
};

const adminRoles = ["super_admin", "admin", "manager", "agent"];
const RECENTS_LIMIT = 5;
const recentsKey = (uid) => `venturaz:recents:${uid || "anon"}`;

function readRecents(uid) {
  try {
    const raw = localStorage.getItem(recentsKey(uid));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENTS_LIMIT) : [];
  } catch {
    return [];
  }
}

function writeRecents(uid, list) {
  try {
    localStorage.setItem(recentsKey(uid), JSON.stringify(list.slice(0, RECENTS_LIMIT)));
  } catch { /* ignore quota */ }
}

function pushRecent(uid, item) {
  const existing = readRecents(uid);
  const dedup = existing.filter((r) => !(r.type === item.type && r.id === item.id));
  const next = [{ ...item, at: Date.now() }, ...dedup].slice(0, RECENTS_LIMIT);
  writeRecents(uid, next);
}

function quickActionsFor(role) {
  if (adminRoles.includes(role)) {
    return [
      { label: "Approve pending KYC", icon: ShieldCheck, route: "/admin/startups", testid: "qa-approve-kyc" },
      { label: "Startup applications", icon: Briefcase, route: "/admin/startups", testid: "qa-startup-apps" },
      { label: "Deal rooms", icon: DoorOpen, route: "/admin/deal-rooms", testid: "qa-deal-rooms" },
      { label: "Manage users", icon: Users, route: "/admin/users", testid: "qa-manage-users" },
    ];
  }
  if (role === "startup") {
    return [
      { label: "Investor matches", icon: Sparkles, route: "/startup/matches", testid: "qa-matches" },
      { label: "My deal rooms", icon: DoorOpen, route: "/startup/deal-rooms", testid: "qa-deal-rooms" },
      { label: "Documents", icon: Briefcase, route: "/startup/documents", testid: "qa-documents" },
    ];
  }
  if (role === "investor") {
    return [
      { label: "Curated Deals", icon: Briefcase, route: "/investor/marketplace", testid: "qa-marketplace" },
      { label: "My interests", icon: Sparkles, route: "/investor/interests", testid: "qa-interests" },
      { label: "My deal rooms", icon: DoorOpen, route: "/investor/deal-rooms", testid: "qa-deal-rooms" },
    ];
  }
  return [];
}

export default function QuickCommand({ open, onOpenChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const actions = quickActionsFor(user?.role);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Build the flat, keyboard-navigable list based on current mode
  const navigable = q.trim() === ""
    ? [
        ...recents.map((r) => ({ kind: "recent", item: r, route: r.route })),
        ...actions.map((a) => ({ kind: "action", item: a, route: a.route })),
      ]
    : results.map((r) => ({ kind: "result", item: r, route: r.route }));

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setRecents(readRecents(user?.user_id));
      setSelectedIdx(0);
      // Focus input shortly after animation starts
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open, user?.user_id]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (!query) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(query)}&limit=10`);
        setResults(r.data?.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  // Reset selection whenever the navigable list changes
  useEffect(() => { setSelectedIdx(0); }, [q, results.length, recents.length]);

  const go = (route, item) => {
    if (item) pushRecent(user?.user_id, item);
    onOpenChange(false);
    nav(route);
  };

  const onInputKeyDown = (e) => {
    if (!navigable.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => (i + 1) % navigable.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => (i - 1 + navigable.length) % navigable.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = navigable[selectedIdx];
      if (!target) return;
      // recents/results carry a Mongo-side item; actions carry a static item (no push)
      const pushable = target.kind === "action" ? null : target.item;
      go(target.route, pushable);
    }
  };

  const clearRecents = (e) => {
    e.stopPropagation();
    writeRecents(user?.user_id, []);
    setRecents([]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="top"
        className="p-0 max-h-[90vh] overflow-hidden bg-white border-b border-navy/10"
        data-testid="quick-command-sheet"
      >
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-navy/10">
            <Search size={18} className="text-ink-soft" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Search startups, investors, deal rooms…"
              className="flex-1 bg-transparent outline-none text-base text-ink placeholder:text-ink-faint"
              data-testid="quick-command-input"
            />
            {loading && <Loader2 size={16} className="animate-spin text-ink-faint" />}
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {q.trim() === "" && (
              <div className="px-4 sm:px-6 py-4 space-y-4">
                {recents.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] tracking-[0.3em] text-ink-faint font-heading flex items-center gap-1.5">
                        <Clock size={11} /> RECENT
                      </div>
                      <button
                        onClick={clearRecents}
                        className="text-[10px] tracking-widest text-ink-faint hover:text-ink transition-colors flex items-center gap-1"
                        data-testid="quick-command-recents-clear"
                      >
                        <X size={11} /> CLEAR
                      </button>
                    </div>
                    <div className="flex flex-col">
                      {recents.map((r, idx) => {
                        const Icon = iconFor[r.type] || Search;
                        const absIdx = idx; // recents come first in navigable list
                        const active = selectedIdx === absIdx;
                        return (
                          <button
                            key={`recent-${r.type}-${r.id}-${idx}`}
                            onClick={() => go(r.route, r)}
                            onMouseEnter={() => setSelectedIdx(absIdx)}
                            className={`w-full flex items-center gap-3 py-2.5 px-2 text-left rounded transition-colors border-b border-navy/5 last:border-0 ${
                              active ? "bg-navy/[0.06] ring-1 ring-navy/10" : "hover:bg-navy/[0.03]"
                            }`}
                            data-testid={`quick-command-recent-${idx}`}
                          >
                            <Icon size={16} className="text-navy" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-ink font-heading truncate">{r.title}</div>
                              <div className="text-xs text-ink-soft truncate">{r.subtitle}</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest text-ink-faint">{r.type.replace("_", " ")}</span>
                            <ArrowRight size={14} className="text-ink-faint" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] tracking-[0.3em] text-ink-faint font-heading mb-2">QUICK ACTIONS</div>
                  <div className="flex flex-col divide-y divide-navy/5">
                    {actions.map((a, idx) => {
                      const Icon = a.icon;
                      const absIdx = recents.length + idx;
                      const active = selectedIdx === absIdx;
                      return (
                        <button
                          key={a.label}
                          onClick={() => go(a.route)}
                          onMouseEnter={() => setSelectedIdx(absIdx)}
                          className={`flex items-center gap-3 py-3 text-left px-2 rounded transition-colors ${
                            active ? "bg-navy/[0.06] ring-1 ring-navy/10" : "hover:bg-navy/[0.03]"
                          }`}
                          data-testid={a.testid}
                        >
                          <Icon size={16} className="text-navy" />
                          <span className="flex-1 text-sm text-ink">{a.label}</span>
                          <ArrowRight size={14} className="text-ink-faint" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {q.trim() !== "" && (
              <div className="px-4 sm:px-6 py-2">
                {!loading && results.length === 0 && (
                  <div className="py-8 text-center text-sm text-ink-faint" data-testid="quick-command-empty">
                    No matches for “{q.trim()}”.
                  </div>
                )}
                {results.map((r, idx) => {
                  const Icon = iconFor[r.type] || Search;
                  const active = selectedIdx === idx;
                  return (
                    <button
                      key={`${r.type}-${r.id}-${idx}`}
                      onClick={() => go(r.route, r)}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      className={`w-full flex items-center gap-3 py-3 px-2 text-left rounded transition-colors border-b border-navy/5 last:border-0 ${
                        active ? "bg-navy/[0.06] ring-1 ring-navy/10" : "hover:bg-navy/[0.03]"
                      }`}
                      data-testid={`quick-command-result-${idx}`}
                    >
                      <Icon size={16} className="text-navy" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink font-heading truncate">{r.title}</div>
                        <div className="text-xs text-ink-soft truncate">{r.subtitle}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-ink-faint">{r.type.replace("_", " ")}</span>
                      <ArrowRight size={14} className="text-ink-faint" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-4 sm:px-6 py-2 border-t border-navy/10 text-[10px] text-ink-faint flex items-center justify-between">
            <span className="hidden sm:inline">
              <kbd className="px-1 py-0.5 bg-navy/5 rounded">↑</kbd>
              <kbd className="px-1 py-0.5 bg-navy/5 rounded ml-1">↓</kbd> navigate
              <kbd className="px-1 py-0.5 bg-navy/5 rounded ml-2">↵</kbd> open
              <kbd className="px-1 py-0.5 bg-navy/5 rounded ml-2">Esc</kbd> close
            </span>
            <span className="sm:hidden">Tap to open</span>
            <span className="uppercase tracking-widest">Quick Command</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
